import { useEditor } from "@tiptap/react";

export const scrollToPos = (editor: ReturnType<typeof useEditor>, from: number) => {
  if (!editor) return;
  const coords = editor.view.coordsAtPos(from);
  if (!coords) return;
  const scrollContainer = editor.view.dom.closest(".overflow-y-scroll") as HTMLElement | null;
  if (scrollContainer) {
    const containerRect = scrollContainer.getBoundingClientRect();
    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop + (coords.top - containerRect.top) - 150,
      behavior: "smooth"
    });
  } else {
    window.scrollTo({
      top: window.scrollY + coords.top - 150,
      behavior: "smooth"
    });
  }
};

// Function to highlight table rows specifically
export const highlightTableRowInEditor = (
  editor: ReturnType<typeof useEditor>,
  originalRow: string,
  _cleanText: string
) => {
  if (!editor || !originalRow) return;

  // Use the table-specific search which has its own progressive fallback
  const tableRowResults = findTableRowInDocument(editor, originalRow);

  if (tableRowResults.length > 0) {
    const match = {
      from: Math.min(...tableRowResults.map((r) => r.from)),
      to: Math.max(...tableRowResults.map((r) => r.to))
    };

    editor.commands.setTextSelection({ from: match.from, to: match.to });
    editor.commands.focus();

    setTimeout(() => scrollToPos(editor, match.from), 100);
  }
};

const findTableRowInDocument = (editor: ReturnType<typeof useEditor>, tableRow: string) => {
  if (!editor) return [];

  const results: Array<{ from: number; to: number }> = [];

  let cells: string[] = [];

  const tdRegex = /<td[^>]*>(.*?)<\/td>/gi;
  const matches = Array.from(tableRow.matchAll(tdRegex));

  cells = matches
    .map((match) => {
      const htmlContent = match[1];
      return htmlContent
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    })
    .filter((cell) => cell.length > 0);

  if (cells.length > 0) {
    const cellAnalysis: Array<{
      text: string;
      cleanText: string;
      matches: Array<{ from: number; to: number; actualText: string }>;
    }> = [];

    for (let i = cells.length - 1; i >= 0; i--) {
      const cellText = cells[i];
      if (cellText.length < 3) continue;

      const cellLower = cellText.toLowerCase();
      const matches: Array<{ from: number; to: number; actualText: string }> = [];

      editor.state.doc.descendants((node, pos: number) => {
        if (node.isText && node.text) {
          const nodeText = node.text.toLowerCase();
          let searchIndex = 0;
          while ((searchIndex = nodeText.indexOf(cellLower, searchIndex)) !== -1) {
            const from = pos + searchIndex;
            const to = from + cellLower.length;
            const actualText = editor.state.doc.textBetween(from, to);
            matches.push({ from, to, actualText });
            searchIndex++;
          }
        }
        return true;
      });

      cellAnalysis.push({
        text: cellText,
        cleanText: cellLower,
        matches
      });
    }

    results.push(
      ...cellAnalysis.flatMap(({ matches }, _, array) => {
        if (!matches[0]) return [];
        if (matches.length === 1) return [{ from: matches[0].from, to: matches[0].to }];

        const anchors = array.filter((cell) => cell.matches.length === 1).map((cell) => cell.matches[0].from);

        if (anchors.length === 0) return [];

        const anchorMidpoint = anchors.reduce((sum, pos) => sum + pos, 0) / anchors.length;
        const closest = matches.reduce((best, match) =>
          Math.abs(match.from - anchorMidpoint) < Math.abs(best.from - anchorMidpoint) ? match : best
        );

        return [{ from: closest.from, to: closest.to }];
      })
    );
  }

  return results;
};

// Main function to highlight any text in the editor
export const highlightTextInEditor = (editor: ReturnType<typeof useEditor>, searchText: string) => {
  if (!editor || !searchText) return;

  // The text is already cleaned by the caller, so use it directly
  const cleanSearchText = searchText.trim();

  // Search directly in the document structure
  const searchResults = findTextInDocument(editor, cleanSearchText);

  if (searchResults.length === 0) {
    return;
  }

  // Use the first match
  const match = searchResults[0];

  // Set selection and highlight
  editor.commands.setTextSelection({ from: match.from, to: match.to });

  editor.commands.focus();

  setTimeout(() => scrollToPos(editor, match.from), 100);
};

// Function to find text in the document with progressive fallback
const findTextInDocument = (editor: ReturnType<typeof useEditor>, searchText: string) => {
  if (!editor) return [];

  const results: Array<{ from: number; to: number }> = [];
  const searchLower = searchText.trim().toLowerCase();
  let found = false;

  // Strategy 1: Exact match search across all text nodes
  editor.state.doc.descendants((node, pos: number) => {
    if (found) return false;

    if (node.isText && node.text) {
      const nodeText = node.text.toLowerCase();
      let searchIndex = 0;

      // Find all occurrences in this node
      while ((searchIndex = nodeText.indexOf(searchLower, searchIndex)) !== -1) {
        const from = pos + searchIndex;
        const to = from + searchLower.length;
        results.push({ from, to });
        found = true;
        searchIndex++; // Continue searching in the same node
      }
    }
    return !found;
  });

  if (found) return results;

  const posMap: number[] = [];
  editor.state.doc.descendants((node, pos: number) => {
    if (node.isText && node.text) {
      for (let i = 0; i < node.text.length; i++) {
        posMap.push(pos + i);
      }
    }
    return true;
  });

  if (posMap.length === 0) return [];

  const docText = editor.state.doc.textContent.toLowerCase();

  let searchIndex = 0;
  while ((searchIndex = docText.indexOf(searchLower, searchIndex)) !== -1) {
    const from = posMap[searchIndex];
    const to = posMap[searchIndex + searchLower.length - 1] + 1;
    results.push({ from, to });
    searchIndex++;
  }

  if (results.length > 0) return results;

  const words = searchLower.split(/\s+/).filter((w) => w.length > 2);
  if (words.length > 0) {
    let bestMatch = { from: -1, to: -1, length: 0 };

    for (let i = words.length; i > 0; i--) {
      const partialText = words.slice(0, i).join(" ");
      if (partialText.length < 5) continue;

      const partialIndex = docText.indexOf(partialText);
      if (partialIndex !== -1 && partialText.length > bestMatch.length) {
        bestMatch = {
          from: posMap[partialIndex],
          to: posMap[partialIndex + partialText.length - 1] + 1,
          length: partialText.length
        };
      }
    }
    if (bestMatch.from !== -1) {
      results.push({ from: bestMatch.from, to: bestMatch.to });
      return results;
    }
  }

  return [];
};
